import { HTMLAttributes } from 'react';
import { Picture as PictureModel, Source } from 'models/Picture';
import Observer from 'components/Shared/Observer';
import cn from 'classnames';
import styles from './styles.scss';

type OwnProps = PictureModel &
  HTMLAttributes<HTMLPictureElement> & {
    wrapperClassName?: string;
  };

function key(s: Source): Source & { key: string } {
  return { ...s, key: s.srcSet };
}

const mapsSrcSetToLazyLoad =
  (isVisible: boolean) =>
  ({
    srcSet,
    ...s
  }: Source & { key: string }): Omit<Source, 'srcSet'> & {
    key: string;
    srcSet?: string;
    ['data-srcset']?: string;
  } => ({
    ...s,
    ['data-srcset']: !isVisible ? srcSet : undefined,
    srcSet: isVisible ? srcSet : undefined
  });

function renderSources(sources: OwnProps['sources'], isVisible: boolean) {
  return (
    sources &&
    sources
      .filter(s => Boolean(s.srcSet))
      .map(key)
      .map(mapsSrcSetToLazyLoad(isVisible))
      .map(({ height, width, ...s }) => <source {...s} key={s.key} />)
  );
}

const initSrcSetsAndSizes = ({ src, sizes, ...props }: State, skip?: boolean) => ({
  ...props,
  ['data-src']: src,
  sizes: skip ? undefined : sizes
});

type State = Omit<OwnProps, 'src' | 'width' | 'height'> & {
  src?: string;
  ['data-src']?: string;
  sizes?: string;
};

function renderImg(
  { sources, height, width, ...props }: OwnProps,
  isVisible: boolean,
  skip?: true
) {
  const propsToUse = isVisible ? props : initSrcSetsAndSizes(props, skip);
  return <img {...propsToUse} />;
}

function picture(props: OwnProps, isVisible: boolean) {
  if (props.sources) {
    const { sources, className, height, width, sizes, src, alt, ...rest } = props;
    return (
      <picture className={cn(className)} {...rest}>
        {renderSources(sources, isVisible)}
        {renderImg(props, isVisible, true)}
      </picture>
    );
  }
  return renderImg(props, isVisible);
}

export const Picture = ({ children, wrapperClassName, ...props }: OwnProps) => {
  const missingHeight = props.sources?.some(source => source.height === 0);

  return (
    <Observer
      type="div"
      threshold={0}
      className={cn(styles.wrapper, wrapperClassName, {
        [styles.missingHeight]: missingHeight
      })}
    >
      {isVisible => (
        <div>
          {picture(props, isVisible)}
          {children && <div>{children}</div>}
        </div>
      )}
    </Observer>
  );
};
